import {Component, EventEmitter, Input, OnInit, Output, ViewEncapsulation} from '@angular/core';
import {Product} from '../../../../models/product.model';
import {OrderItemDelivery} from '../../../../models/order-item-delivery.model';
import {OrderItem} from '../../../../models/order-item.model';
import {ECOMMERCE_TYPE} from '../../../../constants/order-types';
import {ShippingMethod} from '../../../../models/shipping-method';
import {isEmptyArray} from '../../../../shared/utils';
import {OrderService} from '../../../../services/order.service';
import {Order} from '../../../../models/order.model';
import {Currency} from '../../../../interfaces/currency';
import {ConfigService} from '../../../../services/config.service';

@Component({
  selector: 'app-product-list-item-input',
  templateUrl: './product-list-item-input.component.html',
  styleUrls: ['./product-list-item-input.component.scss'],
  encapsulation: ViewEncapsulation.None
})
export class ProductListItemInputComponent implements OnInit {
  hasShippingMethodError = false;
  isSingleShippingMethod = false;
  shippingMethods: any[] = [];

  @Input() orderType: string;
  @Input() orderItem: OrderItem;
  @Input() originalOrderItem: OrderItem;

  @Input() delivery: OrderItemDelivery;
  @Input() collapsed = false;

  @Output() changed = new EventEmitter<void>();

  constructor(private orderService: OrderService, private configService: ConfigService) {
  }

  ngOnInit(): void {
    this.calcAvailableShipmentMethods();
  }


  get hasVariations(): boolean {
    return this.product.hasVariations;
  }

  get isOutOfStock(): boolean {
    if (this.product.inventoryType) {
      return this.product.inStockQuantity === 0;
    }

    return false;
  }




  onQuantityChanged(event) {
    const newValue: number = +event.target.value || 0;
    if (this.orderItem.isAvailable) {
      // ensure quantity is not null
      this.delivery.quantity = this.orderItem.validateDeliveryQuantity(this.delivery, newValue);
    }
  }

  onQuantityBlur() {
    // emit change event only after blur event  to keep focus on input
    this.deliveryChanged();
    return true;
  }

  deliveryChanged( ) {
    this.orderItem.changed = true;
    this.changed.emit();
  }

  get product(): Product {    
    return this.orderItem?.product;
  }

  get canPurchase(): boolean {
     return !this.inputDisabled;
  }

  get isOnDemandProduct(): boolean {
    return !this.product.isBuyingWindow;
  }

  get isItemCut(): boolean{
    return this.orderItem?.product?.item_cut ? this.orderItem?.product?.item_cut : false;
  }

  private  calcAvailableShipmentMethods() {

    const findAvailableShippingMethodsByCountryCode =
      (shippingMethods: ShippingMethod[], countryCode: string): ShippingMethod[] | undefined => {
        return shippingMethods.filter( s => s.country === countryCode);
      }



    if (!this.delivery || !this.product) {
      return;
    }

    this.hasShippingMethodError = false;
    this.isSingleShippingMethod = false;

    const availableShippingMethods =
      findAvailableShippingMethodsByCountryCode(this.product.shipping_methods, this.delivery.country_code);

    if (isEmptyArray(availableShippingMethods)) {
      this.hasShippingMethodError = true;
      this.delivery.method = null;
      return;
    }

    if (availableShippingMethods.length === 1) {
      this.isSingleShippingMethod = true;
    }



    if (!this.delivery.method) {
      // only  if method is not set yet
      this.delivery.method = availableShippingMethods[0].method;
    }


    this.shippingMethods =  availableShippingMethods;


  }

  findMethodLabel(product: Product, delivery: OrderItemDelivery): string {
    if (!product || !delivery) {
      return '';
    }

    const method = delivery.method;
    const addressCountry = delivery.country_code;
    let shippingMethod = null;
    if (method || addressCountry) {
      shippingMethod = product.shipping_methods.find( m => m.method === method && m.country === addressCountry);
    }

    return shippingMethod ? shippingMethod.method_label : '';
  }




  updateShippingMethod(event) {
    if (event) {
      this.delivery.method = event.method;
      this.orderItem.updateDelivery(this.delivery, this.isOnDemandProduct);
      this.changed.emit();
    }
  }

  get canShowInternalOrder(): boolean {
    return this.configService.features.showWBS || this.configService.features.showInternalOrderCodes;
  }

  onInternalOrderChanged(newValue: string) {
    const value  =  !!newValue ? newValue.trim() : '';
    if (this.order.isWBSDefined) {
      this.delivery.wbs = value;
    } else {
      this.delivery.internal_order = value;
    }
    this.orderItem.updateDelivery(this.delivery, this.order.isOnDemand);
    this.changed.emit();
  }

  onInternalOrderReset() {
    this.delivery.wbs = '';
    this.delivery.internal_order = '';
    this.orderItem.updateDelivery(this.delivery, this.order.isOnDemand);
    this.changed.emit();
  }


  get isInStock(): boolean {
    if (this.product.inventoryType) {
      return this.product.inStock;
    }
    return true;
  }

  get orderHasTheSameCurrency(): boolean {
    if  (!this.order.orderCurrency) {
      return  true;
    }

    return this.order.orderCurrency.symbol === this.product.currency.symbol;
  }

  get hasShippingMethods(): boolean {
    return !isEmptyArray(this.shippingMethods);
  }

  get  inputDisabled(): boolean {
    return  this.hasVariations || !this.isInStock || !this.orderHasTheSameCurrency || !this.hasShippingMethods;
  }


  get deliverySubtotal(): number {
    if (isEmptyArray(this.originalOrderItem.deliveries)) {
      return 0;
    }

    if (!this.delivery.id) {
      return 0;
    }


    if (this.originalOrderItem.financials_total.product_total === 0) {
      return 0;
    }

    const originalDelivery = this.originalOrderItem.deliveries.find( d => d.id === this.delivery.id);
    if (!originalDelivery) {
      return 0;
    }

    const totalQuantity = this.originalOrderItem.deliveries.reduce((acc, d) => acc + d.quantity, 0);

    return this.originalOrderItem.financials_total.product_total  *  (originalDelivery.quantity  / totalQuantity);
  }

  get orderItemCurrency(): Currency {
    return this.originalOrderItem.product.currency;
  }


  get order(): Order {
    return this.orderType === ECOMMERCE_TYPE ? this.orderService.activeODOrder : this.orderService.activeBWOrder;
  }
}
